import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getGetTransactionsQueryKey,
  getTransactionsQueryLimitMax,
  TransactionSourceDTO,
  TransactionStateDTO,
  useExportTransactionsHook,
  useGetTransactionsHook,
} from '@/api/trafikportalen';
import Input from '@/components/core/Input.tsx';
import { FilterPanel } from '@/components/FilterPanel';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { VirtualizedList } from '@/components/VirtualizedList';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { Route as TransactionRoute } from '@/routes/$tenantId/_layout/reports/$view.tsx';
import { createSortConfig } from '@/utils/sort';
import { fetchAllPaginatedPages } from '@/helpers/fetchAllPaginatedPages';
import { toApiDateTimeRange } from '@/helpers/formatting';
import {
  applyTransactionListFacetFilters,
  getFilteredSortedTransactionListItems,
  mapBillingTransactionToListItem,
  mapProductTransactionToListItem,
} from '@/helpers/getFilteredSortedTransactionListItems';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import {
  TransactionListItem,
  TransactionListItemData,
} from './TransactionListItem.tsx';
import { useTransactionTranslations } from '@/hooks/useTransactionTranslations.tsx';
import Button from '../core/Button.tsx';
import { IconExportExcel } from '@/icons/index.tsx';
import {
  useExportTransactionsToExcelForTenant,
  useExportTransactionsToExcelForOrg,
} from '@/hooks/useExcelExport.ts';
import { toast } from '@/hooks/useToast';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { Loading } from '../Loading';
import type {
  GetTransactionsParams,
  TransactionWithBillingDetailsDTO,
  TransactionWithProductsDTO,
} from '@/api/trafikportalen';

const getFirstSearchId = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

const isTransactionSource = (value?: string): value is TransactionSourceDTO =>
  Object.values(TransactionSourceDTO).includes(value as TransactionSourceDTO);

const isTransactionState = (value?: string): value is TransactionStateDTO =>
  Object.values(TransactionStateDTO).includes(value as TransactionStateDTO);

interface TransactionListProps {
  departmentsMap?: Record<string, string>;
  organizationsMap?: Record<string, string>;
}

interface ExportParams {
  from?: DateTime;
  to?: DateTime;
  exceededOneYear: boolean;
}

const getDefaultDateFrom = () =>
  DateTime.now().minus({ years: 1 }).plus({ days: 1 }).toFormat('yyyy-MM-dd');

const TransactionList = ({
  departmentsMap,
  organizationsMap,
}: TransactionListProps) => {
  const { formatMessage, locale } = useIntl();
  const isSysAdmin = checkUserPermissions({ permission: 'tenant:viewer' });
  const exportTransactionsToExcelForTenant =
    useExportTransactionsToExcelForTenant();
  const exportTransactionsToExcelForOrg = useExportTransactionsToExcelForOrg();
  const exportTransactionsHook = useExportTransactionsHook();
  const getTransactions = useGetTransactionsHook();
  const rightPanelRef = useRef<HTMLElement>(null);
  const [announcement, setAnnouncement] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportParams, setExportParams] = useState<ExportParams>({
    exceededOneYear: false,
  });

  const {
    query,
    orderPurchaseMethod: filterPurchaseMethod,
    state: filterState,
    sort,
    direction,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
  } = TransactionRoute.useSearch();
  const { getSourceTranslation, getStateTranslation } =
    useTransactionTranslations();

  const defaultDateFrom = useMemo(() => getDefaultDateFrom(), []);
  const selectedSource = getFirstSearchId(filterPurchaseMethod);
  const selectedState = getFirstSearchId(filterState);
  const searchQuery = query?.trim();
  const isDateRangeInverted =
    !!filterDateTo && (filterDateFrom || defaultDateFrom) > filterDateTo;

  const transactionsParams = useMemo((): GetTransactionsParams => {
    const { from, to } = toApiDateTimeRange(
      filterDateFrom || defaultDateFrom,
      filterDateTo,
    );

    return {
      limit: getTransactionsQueryLimitMax,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(isTransactionSource(selectedSource)
        ? { source: selectedSource }
        : {}),
      ...(isTransactionState(selectedState) ? { state: selectedState } : {}),
      ...(searchQuery ? { bobcat_order_id: searchQuery } : {}),
    };
  }, [
    defaultDateFrom,
    filterDateFrom,
    filterDateTo,
    selectedSource,
    selectedState,
    searchQuery,
  ]);

  const {
    items: transactions,
    isLoading,
    isFetchingNextPage,
    fetchNextPageIfNeeded,
  } = usePaginatedList({
    fetchPage: getTransactions,
    params: transactionsParams,
    queryKey: [
      ...getGetTransactionsQueryKey(transactionsParams),
      selectedState ?? '',
      selectedSource ?? '',
    ],
    enabled: !isDateRangeInverted,
  });

  const processedTransactions = useMemo((): TransactionListItemData[] => {
    if (!transactions) return [];

    return transactions.map((row) =>
      mapProductTransactionToListItem(row, departmentsMap, organizationsMap),
    );
  }, [transactions, departmentsMap, organizationsMap]);

  const sortConfigs = useMemo(() => {
    return createSortConfig<TransactionListItemData>({
      textFields: ['created_at'],
    });
  }, []);

  const {
    filterSearch,
    setFilterSearch,
    handleSearchParams,
    handleSearch,
    sortedItems,
  } = useSearchFilterSort<TransactionListItemData>({
    items: processedTransactions || [],
    searchKeys: [],
    sortConfigs,
    routePath: TransactionRoute.fullPath,
    defaultSearchParams: {
      orderPurchaseMethod: [],
      state: [],
      sort: 'created_at',
      direction: 'desc',
      dateFrom: defaultDateFrom,
      dateTo: '',
    },
    searchDebounceMs: 300,
  });

  const optionsPurchaseMethods = useMemo(() => {
    return Object.values(TransactionSourceDTO).map((source) => ({
      id: source,
      label: getSourceTranslation({ source }),
    }));
  }, [getSourceTranslation]);

  const optionsStates = useMemo(() => {
    return Object.values(TransactionStateDTO).map((state) => ({
      id: state,
      label: getStateTranslation({ state }),
    }));
  }, [getStateTranslation]);

  const filteredTransactions = useMemo(() => {
    if (!filterSearch) {
      return processedTransactions;
    }
    return applyTransactionListFacetFilters(sortedItems, filterSearch);
  }, [filterSearch, sortedItems, processedTransactions]);

  const totalAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      const cost =
        transaction.state === TransactionStateDTO.refunded
          ? -transaction.amount_inc_vat
          : transaction.amount_inc_vat;
      return sum + cost;
    }, 0);
  }, [filteredTransactions]);

  const onDropdownChange = useCallback(
    (filter: OptionsType, filterType: 'orderPurchaseMethod' | 'state') => {
      const selected = filter ? [filter] : [];

      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: selected,
      }));

      handleSearchParams({
        [filterType]: selected.map((f) => f.id),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const onInputChange = useCallback(
    (value: string, filterType: 'dateFrom' | 'dateTo') => {
      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: value,
      }));

      handleSearchParams({
        [filterType]: value,
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const renderTransactionListItem = useCallback(
    (transaction: TransactionListItemData) => {
      return (
        <TransactionListItem
          key={`${transaction.id}:${transaction.transaction_product_id}`}
          transaction={transaction}
        />
      );
    },
    [],
  );

  useEffect(() => {
    if (filterSearch) {
      return;
    }

    const purchaseMethodId = getFirstSearchId(filterPurchaseMethod);
    const stateId = getFirstSearchId(filterState);

    const purchaseTypes = optionsPurchaseMethods.filter(
      (d) => d.id === purchaseMethodId,
    );
    const stateTypes = optionsStates.filter((d) => d.id === stateId);

    setFilterSearch({
      query: query ?? '',
      orderPurchaseMethod: purchaseTypes,
      state: stateTypes,
      sort: sort ?? 'created_at',
      direction: direction ?? 'desc',
      dateFrom: filterDateFrom ?? defaultDateFrom,
      dateTo: filterDateTo ?? '',
    });
  }, [
    optionsPurchaseMethods,
    optionsStates,
    filterPurchaseMethod,
    filterState,
    direction,
    query,
    sort,
    filterDateFrom,
    filterDateTo,
    defaultDateFrom,
    filterSearch,
    setFilterSearch,
  ]);

  useEffect(() => {
    let fromDate: DateTime;
    let toDate: DateTime = filterSearch?.dateTo
      ? DateTime.fromISO(filterSearch.dateTo).endOf('day')
      : DateTime.now().endOf('day');

    if (filterSearch?.dateFrom) {
      fromDate = DateTime.fromISO(filterSearch.dateFrom).startOf('day');
    } else if (filteredTransactions.length > 0) {
      const newestTransactionDate = filteredTransactions.at(0)!.created_at;
      const oldestTransactionDate = filteredTransactions.at(-1)!.created_at;
      fromDate = DateTime.fromISO(oldestTransactionDate).startOf('day');
      toDate = DateTime.fromISO(newestTransactionDate).endOf('day');
    } else {
      fromDate = DateTime.now().minus({ years: 1 }).startOf('day');
    }

    const exceededOneYear = toDate.diff(fromDate).as('years') > 1;

    setExportParams((prev) => {
      if (
        prev.from?.toISO() === fromDate.toISO() &&
        prev.to?.toISO() === toDate.toISO() &&
        prev.exceededOneYear === exceededOneYear
      ) {
        return prev;
      }

      return {
        from: fromDate,
        to: toDate,
        exceededOneYear,
      };
    });
  }, [filterSearch, filteredTransactions]);

  // Announce changes to filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredTransactions.length;
    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {transaktion} other {transaktioner}} visas',
      },
      { count },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredTransactions.length, formatMessage]);

  const onExport = useCallback(async () => {
    if (isExporting || !exportParams.from || !exportParams.to) return;

    try {
      setIsExporting(true);

      if (isSysAdmin) {
        const exportedTransactions = await exportTransactionsHook({
          from: exportParams.from.toFormat('yyyy-MM-dd'),
          to: exportParams.to.toFormat('yyyy-MM-dd'),
        });

        const listItems = exportedTransactions.map(
          mapBillingTransactionToListItem,
        );
        const filteredListRows = getFilteredSortedTransactionListItems(
          listItems,
          filterSearch,
        );

        const fullByKey = new Map(
          exportedTransactions.map((row) => [
            `${row.transaction.id}:${row.transaction_product.id}`,
            row,
          ]),
        );

        const orderedForExcel = filteredListRows
          .map((row) =>
            fullByKey.get(`${row.id}:${row.transaction_product_id}`),
          )
          .filter((row): row is TransactionWithBillingDetailsDTO =>
            Boolean(row),
          );

        exportTransactionsToExcelForTenant(orderedForExcel);
      } else {
        const exportedTransactions = await fetchAllPaginatedPages(
          getTransactions,
          transactionsParams,
        );

        const listItems = exportedTransactions.map((row) =>
          mapProductTransactionToListItem(
            row,
            departmentsMap,
            organizationsMap,
          ),
        );
        const filteredListRows = getFilteredSortedTransactionListItems(
          listItems,
          filterSearch,
        );

        const fullByKey = new Map(
          exportedTransactions.map((row) => [
            `${row.transaction.id}:${row.transaction_product.id}`,
            row,
          ]),
        );

        const fromDate = exportParams.from.startOf('day');
        const toDate = exportParams.to.endOf('day');

        const orderedForExcel = filteredListRows
          .map((row) =>
            fullByKey.get(`${row.id}:${row.transaction_product_id}`),
          )
          .filter((row): row is TransactionWithProductsDTO => Boolean(row))
          .filter((t) => {
            const createdAt = DateTime.fromISO(t.transaction.created_at);
            return createdAt >= fromDate && createdAt <= toDate;
          });

        exportTransactionsToExcelForOrg(orderedForExcel, departmentsMap);
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Export misslyckades',
          }),
        },
        { autoClose: 3000 },
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    exportParams.from,
    exportParams.to,
    isSysAdmin,
    exportTransactionsHook,
    exportTransactionsToExcelForTenant,
    getTransactions,
    transactionsParams,
    filterSearch,
    exportTransactionsToExcelForOrg,
    departmentsMap,
    organizationsMap,
    formatMessage,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel ref={rightPanelRef}>
        {filterSearch && (
          <>
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label={formatMessage({ defaultMessage: 'Bobcat order-ID' })}
                  placeholder={formatMessage({
                    defaultMessage: 'Sök exakt Bobcat order-ID',
                  })}
                  id="order-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              }
              filterOptions={[
                <Dropdown
                  key={optionsPurchaseMethods.length}
                  label={formatMessage({ defaultMessage: 'Köpställe' })}
                  id="order-purchase-method"
                  options={optionsPurchaseMethods}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.orderPurchaseMethod?.[0]}
                  onValueChange={(value) =>
                    onDropdownChange(value, 'orderPurchaseMethod')
                  }
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera transaktioner genom att välja köpställe',
                  })}
                  containerRef={rightPanelRef}
                />,
                <Dropdown
                  key={optionsStates.length}
                  label={formatMessage({ defaultMessage: 'Status' })}
                  id="state"
                  options={optionsStates}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.state?.[0]}
                  onValueChange={(value) => onDropdownChange(value, 'state')}
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera transaktioner genom att välja status',
                  })}
                  containerRef={rightPanelRef}
                />,
                <Input
                  type="date"
                  label={formatMessage({ defaultMessage: 'Från datum' })}
                  id="date-from"
                  value={filterSearch.dateFrom || ''}
                  max={filterSearch.dateTo || undefined}
                  onChange={(e) => onInputChange(e.target.value, 'dateFrom')}
                  aria-description={formatMessage({
                    defaultMessage: 'Filtrera transaktioner från detta datum',
                  })}
                />,
                <Input
                  type="date"
                  label={formatMessage({ defaultMessage: 'Till datum' })}
                  id="date-to"
                  value={filterSearch.dateTo || ''}
                  min={filterSearch.dateFrom || defaultDateFrom}
                  error={
                    isDateRangeInverted
                      ? formatMessage({
                          defaultMessage:
                            'Till datum kan inte vara före från datum',
                        })
                      : undefined
                  }
                  onChange={(e) => onInputChange(e.target.value, 'dateTo')}
                  aria-description={formatMessage({
                    defaultMessage: 'Filtrera transaktioner till detta datum',
                  })}
                />,
              ]}
            />
            {exportParams.exceededOneYear && (
              <p className="mt-2 text-sm text-red-700">
                <FormattedMessage defaultMessage="Datumintervallet får inte överstiga 1 år. Välj ett kortare tidsintervall för att kunna exportera." />
              </p>
            )}
            <Button
              startIcon={<IconExportExcel />}
              disabled={
                filteredTransactions.length === 0 ||
                isExporting ||
                isDateRangeInverted ||
                exportParams.exceededOneYear
              }
              loading={isExporting}
              className="mt-2 w-full"
              onClick={onExport}
            >
              {formatMessage({ defaultMessage: 'Exportera till Excel' })}
            </Button>
          </>
        )}
      </RightPanel>
      <VirtualizedList
        count={filteredTransactions.length}
        countName={formatMessage({ defaultMessage: 'transaktioner' })}
        topRightContent={
          <div className="flex items-center gap-1 text-text-950">
            <b>
              {totalAmount.toLocaleString(locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
            </b>
            <FormattedMessage defaultMessage="SEK" />
          </div>
        }
        totalItems={processedTransactions}
        filteredItems={filteredTransactions}
        renderItem={renderTransactionListItem}
        getItemKey={(transaction) =>
          `${transaction.id}:${transaction.transaction_product_id}`
        }
        onNearEnd={fetchNextPageIfNeeded}
        isLoadingMore={isFetchingNextPage}
      />
    </>
  );
};

export default TransactionList;
